import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { features } from '@/config/features';
import { debug } from '@/utils/debug';
import apiClient from '@/services/api/client';

interface UseActiveAccountsCheckOptions {
  /**
   * Component name for debug logging
   */
  componentName: string;
  /**
   * Whether to verify accounts via API (adds ?verify=true param)
   * @default false
   */
  verifyViaAPI?: boolean;
}

interface UseActiveAccountsCheckResult {
  /**
   * Whether the user has active Google Ads accounts
   * null = still checking, true/false = check complete
   */
  hasActiveAccounts: boolean | null;
  /**
   * Whether the account check is in progress
   */
  isCheckingAccounts: boolean;
}

/**
 * Custom hook to check if user has active Google Ads accounts
 *
 * Uses cached value from auth store if available, otherwise falls back to API check.
 * Automatically redirects to home page if no active accounts are found.
 *
 * @example
 * ```tsx
 * const { hasActiveAccounts, isCheckingAccounts } = useActiveAccountsCheck({
 *   componentName: 'Campaign'
 * });
 *
 * if (isCheckingAccounts) {
 *   return <Loading />;
 * }
 * ```
 */
export function useActiveAccountsCheck(
  options: UseActiveAccountsCheckOptions
): UseActiveAccountsCheckResult {
  const { componentName, verifyViaAPI = false } = options;
  const navigate = useNavigate();
  const { hasLinkedGoogleAccounts: cachedHasLinkedAccounts, linkedAccountsReady } = useAuth();
  const [hasActiveAccounts, setHasActiveAccounts] = useState<boolean | null>(null);
  const [isCheckingAccounts, setIsCheckingAccounts] = useState(true);

  useEffect(() => {
    async function checkActiveAccounts() {
      // If verifyViaAPI=false, skip the check entirely - assume user has accounts
      // This prevents hanging on page reload when we don't need to verify
      if (!verifyViaAPI) {
        debug.log(`${componentName}: Skipping account check (verifyViaAPI=false), assuming user has accounts`);
        setHasActiveAccounts(true);
        setIsCheckingAccounts(false);
        return;
      }

      // If cache is enabled and data is ready, use cached value
      if (features.useLinkedAccountsCache && linkedAccountsReady) {
        debug.log(`${componentName}: Using cached linked accounts status`, {
          cachedHasLinkedAccounts,
          linkedAccountsReady,
          willRedirect: !cachedHasLinkedAccounts
        });
        setHasActiveAccounts(cachedHasLinkedAccounts);
        setIsCheckingAccounts(false);

        if (!cachedHasLinkedAccounts) {
          debug.log(`${componentName}: No linked accounts in cache (cachedHasLinkedAccounts=${cachedHasLinkedAccounts}), redirecting to home`);
          navigate('/', { replace: true });
        }
        return;
      }

      // If cache is enabled but not ready yet, wait for it (don't make API call)
      if (features.useLinkedAccountsCache && !linkedAccountsReady) {
        debug.log(`${componentName}: Cache enabled but not ready, waiting...`);
        return; // Keep loading state, will re-run when linkedAccountsReady changes
      }

      // Otherwise, check via API (cache disabled or verifyViaAPI=true)
      try {
        debug.log(`${componentName}: Checking for active Google Ads accounts via API`);
        const params = verifyViaAPI ? { verify: true } : {};
        const response = await apiClient.get('/google-ads/has-active-accounts', { params });
        const hasAccounts = response.data?.has_active_accounts || false;

        debug.log(`${componentName}: Active accounts check result`, { hasAccounts });
        setHasActiveAccounts(hasAccounts);

        if (!hasAccounts) {
          debug.log(`${componentName}: No active accounts, redirecting to home`);
          navigate('/', { replace: true });
        }
      } catch (error) {
        debug.error(`${componentName}: Failed to check active accounts`, error);
        setHasActiveAccounts(false);
        navigate('/', { replace: true });
      } finally {
        setIsCheckingAccounts(false);
      }
    }

    checkActiveAccounts();
  }, [navigate, cachedHasLinkedAccounts, linkedAccountsReady, componentName, verifyViaAPI]);

  return { hasActiveAccounts, isCheckingAccounts };
}
